/**
 * Behavioral Code Service
 *
 * Thin wrapper around centralized api.js for behavioral code operations.
 * All API calls use the centralized service for consistent auth, retry, and error handling.
 */

import api from '../../../services/api';

// ============================================================================
// Code Registry Operations
// ============================================================================

/**
 * List all behavioral codes
 * @param {Object} options - Query options
 * @param {string} options.category - Filter by category
 * @param {string} options.dimension - Filter by primary dimension
 * @param {boolean} options.includeInactive - Include inactive codes
 */
export const listCodes = async (options = {}) => {
  return api.listBehavioralCodes(options);
};

/**
 * Get a single behavioral code by ID
 */
export const getCodeById = async (codeId) => {
  return api.getBehavioralCodeById(codeId);
};

/**
 * Get categories with code counts
 */
export const getCategories = async () => {
  return api.getBehavioralCategories();
};

/**
 * Get dimensions with mapped codes
 */
export const getDimensions = async () => {
  return api.getBehavioralDimensions();
};

/**
 * Create a new behavioral code (admin only)
 */
export const createCode = async (codeData) => {
  return api.createBehavioralCode(codeData);
};

/**
 * Update a behavioral code (admin only)
 */
export const updateCode = async (codeId, updates) => {
  return api.updateBehavioralCode(codeId, updates);
};

/**
 * Delete (soft) a behavioral code (admin only)
 */
export const deleteCode = async (codeId) => {
  return api.deleteBehavioralCode(codeId);
};

// ============================================================================
// Chunk Code Operations
// ============================================================================

/**
 * Get behavioral codes applied to a chunk
 */
export const getChunkCodes = async (chunkId) => {
  return api.getChunkBehavioralCodes(chunkId);
};

/**
 * Apply a behavioral code to a chunk
 * @param {string} chunkId - The chunk ID
 * @param {Object} codeData - Code application data
 * @param {string} codeData.code - The code name (e.g., "OWNERSHIP_OF_IMPACT")
 * @param {number} codeData.confidence - Confidence score (0-1)
 * @param {string} codeData.notes - Optional notes
 * @param {string} codeData.taxonomy - Optional taxonomy (default: faana_core)
 */
export const applyCodeToChunk = async (chunkId, codeData) => {
  return api.applyBehavioralCodeToChunk(chunkId, codeData);
};

/**
 * Update a code on a chunk (notes, confidence)
 */
export const updateChunkCode = async (chunkId, codeName, updates) => {
  return api.updateChunkBehavioralCode(chunkId, codeName, updates);
};

/**
 * Remove a code from a chunk
 * @param {string} chunkId - The chunk ID
 * @param {string} codeName - The code name to remove
 * @param {string} entityId - Entity ID for access validation
 */
export const removeCodeFromChunk = async (chunkId, codeName, entityId) => {
  return api.removeChunkBehavioralCode(chunkId, codeName, entityId);
};

// ============================================================================
// Document/Chunk Query Operations
// ============================================================================

/**
 * Search documents with behavioral code filters
 * @param {string} entityId - Entity ID
 * @param {Object} options - Search options
 */
export const searchDocuments = async (entityId, options = {}) => {
  return api.searchBehavioralDocuments(entityId, options);
};

/**
 * Get all chunks for a document
 */
export const getDocumentChunks = async (entityId, sourceId) => {
  return api.getDocumentBehavioralChunks(entityId, sourceId);
};

/**
 * Get documents for an entity (unique source IDs)
 */
export const getEntityDocuments = async (entityId) => {
  return api.getEntityBehavioralDocuments(entityId);
};

// ============================================================================
// AI Suggestion Operations
// ============================================================================

/**
 * Get AI suggestions for behavioral codes on a chunk
 * @param {string} chunkId - The chunk ID
 * @param {string} entityId - Entity ID for access validation
 * @param {Object} options - Optional parameters
 * @param {boolean} options.includeExplanation - Include reasoning (default: true)
 * @param {number} options.minConfidence - Minimum confidence (default: 0.5)
 * @param {string} options.entityContext - Speaker/subject context
 */
export const getAISuggestions = async (chunkId, entityId, options = {}) => {
  return api.getBehavioralAISuggestions(chunkId, entityId, options);
};

/**
 * Get AI suggestions for all chunks in a document
 * @param {string} sourceId - The document source ID
 * @param {string} entityId - Entity ID for access validation
 * @param {Object} options - Optional parameters
 */
export const getBulkAISuggestions = async (sourceId, entityId, options = {}) => {
  return api.getBulkBehavioralAISuggestions(sourceId, entityId, options);
};

// ============================================================================
// Behavioral Scoring Operations
// ============================================================================

/**
 * Get behavioral scores for an entity
 * @param {string} entityId - Entity ID
 * @param {Object} options - Optional parameters
 * @param {number} options.timeWindow - 30, 60, 90, or 120 days (default: 90)
 * @param {boolean} options.includeDimensions - Include per-dimension scores (default: true)
 * @param {boolean} options.includeHistory - Include historical scores (default: false)
 * @param {boolean} options.includeCodeDistribution - Include top codes (default: true)
 */
export const getEntityScores = async (entityId, options = {}) => {
  return api.getEntityBehavioralScores(entityId, options);
};

export default {
  // Code registry
  listCodes,
  getCodeById,
  getCategories,
  getDimensions,
  createCode,
  updateCode,
  deleteCode,

  // Chunk operations
  getChunkCodes,
  applyCodeToChunk,
  updateChunkCode,
  removeCodeFromChunk,

  // Search/query
  searchDocuments,
  getDocumentChunks,
  getEntityDocuments,

  // AI suggestions
  getAISuggestions,
  getBulkAISuggestions,

  // Scoring
  getEntityScores,
};
